'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useTheme } from '@/contexts/ThemeContext'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import { useAuthStore } from '@/store/useAuthStore'
import { ShoppingBag, Search, Heart, MapPin, Plus, Filter, X } from 'lucide-react'
import Link from 'next/link'
import Image from 'next/image'
import { parseAuthToken } from '@/utils/base64'
import logger from '@/utils/logger'

type MarketCategory = {
  id: string
  name: string
  description: string
  icon: string
  display_order: number
}

type MarketItem = {
  id: string
  seller_id: string
  category_id: string
  title: string
  description: string
  price: number
  brand?: string
  model?: string
  condition?: string
  transaction_type: string
  region?: string
  status: string
  view_count: number
  created_at: string
  market_images?: Array<{
    id: string
    image_url: string
    is_primary: boolean
    display_order: number
  }>
  market_categories?: {
    name: string
    icon: string
  }
  user_profiles?: {
    name: string
    profile_image?: string
    certification_level?: string
  }
  is_favorited?: boolean
  favorite_count?: number
}

export default function MarketPage() {
  const router = useRouter()
  const { theme } = useTheme()
  const { user } = useAuthStore()
  const [profile, setProfile] = useState<any>(null)

  const [categories, setCategories] = useState<MarketCategory[]>([])
  const [items, setItems] = useState<MarketItem[]>([])
  const [loading, setLoading] = useState(true)

  const [selectedCategory, setSelectedCategory] = useState<string>('전체')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedRegion, setSelectedRegion] = useState<string>('전체')
  const [showFilterModal, setShowFilterModal] = useState(false)

  const [minPrice, setMinPrice] = useState<string>('')
  const [maxPrice, setMaxPrice] = useState<string>('')
  const [selectedCondition, setSelectedCondition] = useState<string>('전체')
  const [selectedTransactionType, setSelectedTransactionType] = useState<string>('전체')

  useEffect(() => {
    loadProfile()
    loadCategories()
  }, [])

  useEffect(() => {
    loadItems()
  }, [profile, selectedCategory, searchQuery, selectedRegion, minPrice, maxPrice, selectedCondition, selectedTransactionType])

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) return

      const decoded = parseAuthToken(token)
      if (!decoded) return

      const supabase = getSupabaseClient()
      let query = supabase.from('user_profiles').select('*')

      if (decoded.kakao_id) {
        query = query.eq('kakao_id', decoded.kakao_id)
      } else if (decoded.naver_id) {
        query = query.eq('naver_id', decoded.naver_id)
      } else {
        return
      }

      const { data } = await query.single()
      if (data) {
        setProfile(data)
      }
    } catch (error) {
      logger.error('Error loading profile:', error)
    }
  }

  const loadCategories = async () => {
    try {
      const supabase = getSupabaseClient()
      const { data, error } = await supabase
        .from('market_categories')
        .select('*')
        .order('display_order', { ascending: true })

      if (error) throw error
      setCategories(data || [])
    } catch (error) {
      logger.error('Error loading categories:', error)
    }
  }

  const loadItems = async () => {
    setLoading(true)
    try {
      const supabase = getSupabaseClient()

      // Get current user ID for favorites
      let currentUserId = null
      if (profile) {
        currentUserId = profile.id
      }

      let query = supabase
        .from('market_items')
        .select(`
          *,
          market_categories (name, icon),
          user_profiles (name, profile_image, certification_level),
          market_images (id, image_url, is_primary, display_order)
        `)
        .eq('status', 'available')
        .order('created_at', { ascending: false })

      // Category filter
      if (selectedCategory !== '전체') {
        const category = categories.find(c => c.name === selectedCategory)
        if (category) {
          query = query.eq('category_id', category.id)
        }
      }

      // Search filter
      if (searchQuery.trim()) {
        query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%,brand.ilike.%${searchQuery}%,model.ilike.%${searchQuery}%`)
      }

      // Region filter
      if (selectedRegion !== '전체') {
        query = query.eq('region', selectedRegion)
      }

      // Price range filter
      if (minPrice) {
        query = query.gte('price', parseInt(minPrice))
      }
      if (maxPrice) {
        query = query.lte('price', parseInt(maxPrice))
      }

      // Condition filter
      if (selectedCondition !== '전체') {
        query = query.eq('condition', selectedCondition)
      }

      // Transaction type filter
      if (selectedTransactionType !== '전체') {
        if (selectedTransactionType === 'both') {
          query = query.eq('transaction_type', 'both')
        } else {
          query = query.or(`transaction_type.eq.${selectedTransactionType},transaction_type.eq.both`)
        }
      }

      const { data, error } = await query

      if (error) throw error

      // Get favorites for current user
      let favoriteItemIds: Set<string> = new Set()
      if (currentUserId) {
        const { data: favData } = await supabase
          .from('market_favorites')
          .select('item_id')
          .eq('user_id', currentUserId)

        if (favData) {
          favoriteItemIds = new Set(favData.map(f => f.item_id))
        }
      }

      // Get favorite counts for all items
      const itemIds = data?.map(item => item.id) || []
      const favoriteCounts = new Map<string, number>()

      if (itemIds.length > 0) {
        const { data: countData } = await supabase
          .from('market_favorites')
          .select('item_id')
          .in('item_id', itemIds)

        if (countData) {
          countData.forEach(fav => {
            favoriteCounts.set(fav.item_id, (favoriteCounts.get(fav.item_id) || 0) + 1)
          })
        }
      }

      const enrichedData = (data || []).map(item => ({
        ...item,
        is_favorited: favoriteItemIds.has(item.id),
        favorite_count: favoriteCounts.get(item.id) || 0
      }))

      setItems(enrichedData)
    } catch (error) {
      logger.error('Error loading items:', error)
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  const toggleFavorite = async (itemId: string, e: React.MouseEvent) => {
    e.stopPropagation()

    if (!profile) {
      router.push('/login')
      return
    }

    try {
      const supabase = getSupabaseClient()
      const item = items.find(i => i.id === itemId)

      if (!item) return

      if (item.is_favorited) {
        // Remove favorite
        await supabase
          .from('market_favorites')
          .delete()
          .eq('user_id', profile.id)
          .eq('item_id', itemId)
      } else {
        // Add favorite
        await supabase
          .from('market_favorites')
          .insert({
            user_id: profile.id,
            item_id: itemId
          })
      }

      // Reload items to update favorite status
      loadItems()
    } catch (error) {
      logger.error('Error toggling favorite:', error)
    }
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ko-KR').format(price) + '원'
  }

  const getTransactionTypeLabel = (type: string) => {
    switch (type) {
      case 'direct': return '직거래'
      case 'delivery': return '택배'
      case 'both': return '직거래/택배'
      default: return type
    }
  }

  const regions = ['전체', '서울', '경기', '인천', '강원', '충북', '충남', '대전', '세종', '경북', '경남', '대구', '울산', '부산', '전북', '전남', '광주', '제주']
  const conditions = ['전체', '최상', '상', '중', '하']
  const transactionTypes = ['전체', 'direct', 'delivery', 'both']

  return (
    <div className={`min-h-screen ${theme === 'light' ? 'bg-[#f4f7fb]' : 'bg-[#0f172a]'}`}>
      {/* Header */}
      <div className={`sub-sticky z-20 backdrop-blur-xl border-b ${
        theme === 'light' ? 'bg-white/80 border-gray-200' : 'bg-gray-900/80 border-gray-800'
      }`}>
        <div className="max-w-7xl mx-auto px-3 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-[#2b6df0]" />
              <h1 className={`text-lg font-bold ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
                버디마켓
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <Link
                href="/market/favorites"
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border ${
                  theme === 'light'
                    ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <Heart className="w-3.5 h-3.5" />
                <span className="text-xs">지 목록</span>
              </Link>
              <button
                onClick={() => setShowFilterModal(true)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border ${
                  theme === 'light'
                    ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <Filter className="w-3.5 h-3.5" />
                <span className="text-xs">필터</span>
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="상품명, 브랜드, 모델 검색"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-9 pr-3 py-2.5 rounded-lg border text-sm ${
                theme === 'light'
                  ? 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  : 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
              } focus:outline-none focus:ring-2 focus:ring-[#2b6df0]`}
            />
          </div>

          {/* Category Tabs */}
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setSelectedCategory('전체')}
              className={`px-3 py-1.5 rounded-lg whitespace-nowrap transition-all text-xs ${
                selectedCategory === '전체'
                  ? theme === 'light'
                    ? 'bg-[#0a1f3a] text-white'
                    : 'bg-[#1e3a5f] text-white'
                  : theme === 'light'
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              전체
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.name)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg whitespace-nowrap transition-all text-xs ${
                  selectedCategory === category.name
                    ? theme === 'light'
                      ? 'bg-[#0a1f3a] text-white'
                      : 'bg-[#1e3a5f] text-white'
                    : theme === 'light'
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <span className="text-sm">{category.icon}</span>
                <span className="font-medium">{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Items Grid */}
      <div className="max-w-7xl mx-auto px-3 py-4">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className={`rounded-xl overflow-hidden ${
                  theme === 'light' ? 'bg-gray-200' : 'bg-gray-800'
                } animate-pulse`}
              >
                <div className="aspect-square bg-gray-300 dark:bg-gray-700" />
                <div className="p-3">
                  <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded mb-2" />
                  <div className="h-6 bg-gray-300 dark:bg-gray-700 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingBag className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className={`text-lg font-medium mb-2 ${
              theme === 'light' ? 'text-gray-700' : 'text-gray-300'
            }`}>
              등록된 상품이 없습니다
            </h3>
            <p className={`text-sm mb-6 ${
              theme === 'light' ? 'text-gray-500' : 'text-gray-400'
            }`}>
              첫 번째 상품을 등록해보세요!
            </p>
            <Link
              href={profile ? '/market/new' : '/login'}
              className="inline-flex items-center gap-2 bg-[#0a1f3a] text-white px-5 py-2.5 rounded-xl transition-all text-sm"
            >
              <Plus className="w-4 h-4" />
              상품 등록하기
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
            {items.map((item) => {
              const primaryImage = item.market_images?.find(img => img.is_primary) || item.market_images?.[0]

              return (
                <div
                  key={item.id}
                  onClick={() => router.push(`/market/${item.id}`)}
                  className={`rounded-xl overflow-hidden cursor-pointer transition-all hover:shadow-xl ${
                    theme === 'light'
                      ? 'bg-white border border-gray-200 hover:border-[#2b6df0]'
                      : 'bg-gray-800 border border-gray-700 hover:border-[#2b6df0]'
                  }`}
                >
                  {/* Image */}
                  <div className="relative aspect-square">
                    {primaryImage?.image_url ? (
                      <img
                        src={primaryImage.image_url}
                        alt={item.title}
                        className="w-full h-full object-cover bg-transparent"
                      />
                    ) : (
                      <div className={`w-full h-full flex items-center justify-center ${
                        theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'
                      }`}>
                        <ShoppingBag className="w-8 h-8 md:w-12 md:h-12 text-gray-400" />
                      </div>
                    )}
                    {/* Favorite Button */}
                    <button
                      onClick={(e) => toggleFavorite(item.id, e)}
                      className="absolute top-2 right-2 p-2 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm transition-all hover:scale-110"
                    >
                      <Heart
                        className={`w-5 h-5 ${
                          item.is_favorited
                            ? 'fill-red-500 text-red-500'
                            : 'text-gray-600 dark:text-gray-400'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Info */}
                  <div className="p-2">
                    <div className="flex items-center gap-1 mb-1.5">
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#e9eff7] dark:bg-[rgba(43,109,240,0.15)] text-[#2b6df0] dark:text-[#5ea6ff]">
                        {item.market_categories?.name || '기타'}
                      </span>
                      {item.condition && (
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">
                          {item.condition}
                        </span>
                      )}
                    </div>

                    <h3 className={`text-xs md:text-sm font-semibold mb-1 line-clamp-2 ${
                      theme === 'light' ? 'text-gray-900' : 'text-white'
                    }`}>
                      {item.title}
                    </h3>

                    <p className="text-sm md:text-base font-bold text-[#2b6df0] dark:text-[#5ea6ff] mb-1.5">
                      {formatPrice(item.price)}
                    </p>

                    <div className="flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-0.5">
                        <MapPin className="w-2.5 h-2.5" />
                        <span className="truncate">{item.region || '미정'}</span>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <Heart className="w-2.5 h-2.5" />
                        <span>{item.favorite_count || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowFilterModal(false)}
          />
          <div className={`relative w-full max-w-sm max-h-[70vh] overflow-y-auto rounded-2xl shadow-2xl ${
            theme === 'light' ? 'bg-white' : 'bg-gray-800'
          }`}>
            <div className="sub-sticky z-10 flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <h3 className={`text-base font-bold ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
                필터
              </h3>
              <button
                onClick={() => setShowFilterModal(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-3 space-y-4">
              {/* Region Filter */}
              <div>
                <label className={`block text-xs font-semibold mb-1.5 ${
                  theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                }`}>
                  지역
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {regions.map((region) => (
                    <button
                      key={region}
                      onClick={() => setSelectedRegion(region)}
                      className={`px-2.5 py-1 rounded-lg text-xs transition-all ${
                        selectedRegion === region
                          ? 'bg-[#0a1f3a] text-white'
                          : theme === 'light'
                            ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {region}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div>
                <label className={`block text-xs font-semibold mb-1.5 ${
                  theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                }`}>
                  가격대
                </label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    placeholder="최소 가격"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    className={`w-28 px-2 py-1.5 rounded-lg border text-xs ${
                      theme === 'light'
                        ? 'bg-white border-gray-300 text-gray-900'
                        : 'bg-gray-700 border-gray-600 text-white'
                    } focus:outline-none focus:ring-1 focus:ring-[#2b6df0]`}
                  />
                  <span className="text-gray-500 text-xs">~</span>
                  <input
                    type="number"
                    placeholder="최대 가격"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    className={`w-28 px-2 py-1.5 rounded-lg border text-xs ${
                      theme === 'light'
                        ? 'bg-white border-gray-300 text-gray-900'
                        : 'bg-gray-700 border-gray-600 text-white'
                    } focus:outline-none focus:ring-1 focus:ring-[#2b6df0]`}
                  />
                </div>
              </div>

              {/* Condition Filter */}
              <div>
                <label className={`block text-xs font-semibold mb-1.5 ${
                  theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                }`}>
                  상품 상태
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {conditions.map((condition) => (
                    <button
                      key={condition}
                      onClick={() => setSelectedCondition(condition)}
                      className={`px-2.5 py-1 rounded-lg text-xs transition-all ${
                        selectedCondition === condition
                          ? 'bg-[#0a1f3a] text-white'
                          : theme === 'light'
                            ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {condition}
                    </button>
                  ))}
                </div>
              </div>

              {/* Transaction Type Filter */}
              <div>
                <label className={`block text-xs font-semibold mb-1.5 ${
                  theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                }`}>
                  거래 방법
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {transactionTypes.map((type) => (
                    <button
                      key={type}
                      onClick={() => setSelectedTransactionType(type)}
                      className={`px-2.5 py-1 rounded-lg text-xs transition-all ${
                        selectedTransactionType === type
                          ? 'bg-[#0a1f3a] text-white'
                          : theme === 'light'
                            ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {getTransactionTypeLabel(type)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reset Button */}
              <button
                onClick={() => {
                  setSelectedRegion('전체')
                  setMinPrice('')
                  setMaxPrice('')
                  setSelectedCondition('전체')
                  setSelectedTransactionType('전체')
                }}
                className={`w-full py-2 rounded-lg border text-sm ${
                  theme === 'light'
                    ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                }`}
              >
                필터 초기화
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Add Button */}
      <Link
        href={profile ? '/market/new' : '/login'}
        className="fixed right-3 bottom-20 md:bottom-8 z-30 flex items-center gap-1.5 bg-[#0a1f3a] text-white px-3.5 py-2.5 rounded-full shadow-xl transition-all"
      >
        <Plus className="w-4 h-4" />
        <span className="font-bold text-sm">상품 등록</span>
      </Link>
    </div>
  )
}
